import { Area } from 'react-easy-crop';

/**
 * Creates a cropped image from a source image and crop area
 * @param imageSrc - The source image URL or data URL
 * @param crop - The crop area from react-easy-crop
 * @param fileName - Optional file name for the output
 * @returns A File object containing the cropped image
 */
export async function getCroppedImg(
  imageSrc: string,
  crop: Area,
  fileName = 'avatar.jpg'
): Promise<File> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Set canvas size to crop size
      canvas.width = crop.width;
      canvas.height = crop.height;

      // Draw the cropped image
      ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          // Create a File from the blob
          const file = new File([blob], fileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          resolve(file);
        },
        'image/jpeg',
        0.95 // JPEG quality
      );
    };
    image.onerror = () => {
      reject(new Error('Failed to load image'));
    };
  });
}

/**
 * Reads a File as a data URL
 * @param file - The file to read
 * @returns A promise that resolves with the data URL
 */
export function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file as string'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Validates an image file
 * @param file - The file to validate
 * @returns An object with validation result and optional error message
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload a JPEG, PNG, or WebP image.'
    };
  }

  // Check file size (10MB max)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File is too large. Maximum size is 10MB.'
    };
  }

  return { valid: true };
}

export interface PresignedUploadOptions {
  /** Called with the completed percentage (0-100) as the body is sent. */
  onProgress?: (percent: number) => void;
  /** Aborts the in-flight upload — a chat attachment removed while it uploads. */
  signal?: AbortSignal;
}

/**
 * Uploads a file to a presigned URL.
 *
 * Uses `XMLHttpRequest` rather than `fetch` for one reason: it is still the only
 * browser API that reports UPLOAD progress. A chat attachment can be ten
 * megabytes, and a chip that cannot say how far along it is looks hung.
 *
 * @param presignedUrl - The presigned URL
 * @param fields - The form fields for the presigned POST
 * @param file - The file to upload
 * @param options - Progress reporting and cancellation
 * @returns A promise that resolves when the upload is complete
 */
export function uploadToPresignedUrl(
  presignedUrl: string,
  fields: Record<string, string>,
  file: File,
  options?: PresignedUploadOptions
): Promise<void> {
  const formData = new FormData();

  // Append all the presigned fields
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  // Append the file last (as per S3 requirements)
  formData.append('file', file);

  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', presignedUrl);

    const abort = () => request.abort();
    options?.signal?.addEventListener('abort', abort);
    const settle = (finish: () => void) => {
      options?.signal?.removeEventListener('abort', abort);
      finish();
    };

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        options?.onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    };
    request.onload = () =>
      settle(() =>
        request.status >= 200 && request.status < 300
          ? resolve()
          : reject(new Error(`Upload failed: ${request.status} ${request.statusText}`))
      );
    request.onerror = () => settle(() => reject(new Error('Upload failed')));
    request.onabort = () => settle(() => reject(new DOMException('Upload aborted', 'AbortError')));

    // A draft removed while its presign was still in flight aborts a signal that
    // is already aborted by the time we get here, and `addEventListener` never
    // fires for that — without this check the bytes would upload anyway.
    if (options?.signal?.aborted) {
      return settle(() => reject(new DOMException('Upload aborted', 'AbortError')));
    }

    request.send(formData);
  });
}

/**
 * Triggers a browser download of a Blob under the given filename.
 * Wraps the object-URL → anchor → click → revoke dance.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
